import { PropsWithChildren, FC, useEffect, useState } from 'react'
import { SearchOutlined } from '@ant-design/icons'
import { Modal, Select, Row, Col, message } from 'antd'
import { AddFollowerType, EligibleFollower } from '@OpsQuality/types'
import jobDetailsStyles from '@ProdWb/JobDetails/styles.css'

import Box from '~/core/Box'
import Text from '~/core/Text'
import Button from '~/core/Button'
import SecurePlusIcon from '~/components/SecurePlusIcon'
import { FollowerUserDetailsInput } from '~/__generated__/globalTypes'
import {
    FollowerUser,
    ActiveMfgDrisQuery,
    DriFragment as MfgDriType,
} from '~/gql/graphql'
import CurrentFollower from '../CurrentFollowers'
import { MutationOperations, getFollowersObject } from '../logic'
import colors from '~/styles/_colors.css'

import followersStyle from './styles.css'

const { Option } = Select

type AddFollowersType = {
    showModal: () => void
    isModalVisible: boolean
    handleCancel: () => void
    jobName?: string
    followers?: FollowerUser[]
    isSecurePlus?: boolean
    mfgDri?: ActiveMfgDrisQuery['getActiveMfgDriUsers'] | null
    eligibleFollowers?: EligibleFollower[]
    jobRequestId?: number | null
}

const AddFollowersModal: FC<PropsWithChildren<AddFollowersType>> = ({
    isModalVisible,
    handleCancel,
    jobName,
    isSecurePlus,
    followers,
    mfgDri,
    eligibleFollowers,
    jobRequestId,
}) => {
    const [followersList, setFollowersList] = useState<AddFollowerType[]>([])
    const [addedFollowers, setFollowers] = useState<number[]>([])
    const [eligibleFollowersList, setEligibleFollowersList] =
        useState<EligibleFollower[]>()

    const filterEligibleFollowers = (
        currentEligibleFollowers: EligibleFollower[] | undefined,
        currentFollowers: AddFollowerType[],
    ) => {
        return currentEligibleFollowers?.filter(
            (eligibleFollower: EligibleFollower) =>
                currentFollowers.every(
                    currentFollower =>
                        eligibleFollower['VALUE'] !== currentFollower['id'],
                ),
        )
    }

    useEffect(() => {
        const currentEligibleFollowers = eligibleFollowers
        const currentFollowers = followers
            ? followers.map(follower => {
                  return getFollowersObject(
                      follower,
                      mfgDri as unknown as MfgDriType,
                      eligibleFollowers,
                  )
              })
            : []

        const filteredEligibleFollowers = filterEligibleFollowers(
            currentEligibleFollowers,
            currentFollowers,
        )

        if (filteredEligibleFollowers) {
            setEligibleFollowersList(filteredEligibleFollowers)
        }
        setFollowersList(currentFollowers)
    }, [eligibleFollowers, followers, mfgDri])
    const useFollowerMethods = MutationOperations()

    const handleChange = (value: number[]) => {
        setFollowers(value)
    }

    const handleCloseModal = () => {
        const currentFollowers = followers
            ? followers.map(follower => {
                  return getFollowersObject(
                      follower,
                      mfgDri as unknown as MfgDriType,
                      eligibleFollowers,
                  )
              })
            : []

        setFollowersList([...currentFollowers])

        const filteredEligibleFollowers = filterEligibleFollowers(
            eligibleFollowers,
            currentFollowers,
        )

        if (filteredEligibleFollowers) {
            setEligibleFollowersList(filteredEligibleFollowers)
        }

        setFollowers([])
        handleCancel()
    }

    const handleSubmit = async () => {
        const addedFollowersList: FollowerUserDetailsInput[] = []
        const removefollowers: FollowerUserDetailsInput[] = []

        followersList.map((follower: AddFollowerType) => {
            if (!follower.delete && follower.id && follower.isAdded) {
                addedFollowersList.push({
                    id: follower.partnerId,
                    name: follower.followerName,
                })
            }

            if (follower.delete && !follower.isAdded) {
                removefollowers.push({
                    id: follower.partnerId,
                    name: follower.followerName,
                })
            }
        })

        if (
            jobRequestId &&
            (addedFollowersList.length || removefollowers.length)
        ) {
            const result = await useFollowerMethods.updateFollowersList(
                jobRequestId,
                addedFollowersList,
                removefollowers,
            )

            if (result) {
                message.info(
                    'Please allow up to 3 minutes for changes to sync with Odoo',
                    5,
                )
                handleCloseModal()
            } else {
                message.error('Failed to update followers')
            }
        } else {
            message.error('Add or remove atleast one follower to proceed')
        }
    }

    const handleAdd = () => {
        const addedFollowersList = followersList

        addedFollowers.forEach((follower: number) => {
            const followerObject = eligibleFollowers?.find(
                (eligibleFollower: { VALUE: number }) =>
                    eligibleFollower.VALUE === follower,
            )
            const followersIndex = followersList.findIndex(
                (followerObj: AddFollowerType) =>
                    followerObj.id ===
                    parseInt(followerObject?.VALUE.toString() || ''),
            )

            if (followersIndex < 0) {
                const inputObject = {
                    id: followerObject?.VALUE || null,
                    followerName: followerObject?.LABEL || '',
                    partnerId: Number(followerObject?.USER_ID) || 0,
                    assigned: false,
                    delete: false,
                    canDelete: true,
                    isAdded: true,
                    isChannel: false,
                }

                addedFollowersList.push(inputObject)
            }
        })

        setFollowersList([...addedFollowersList])
        const filteredEligibleFollowers = filterEligibleFollowers(
            eligibleFollowersList,
            addedFollowersList,
        )

        if (filteredEligibleFollowers) {
            setEligibleFollowersList(filteredEligibleFollowers)
        }

        setFollowers([])
    }

    const handleRemove = (id: number) => {
        const currentArray = followersList

        const followerIndex = currentArray.findIndex(
            (currentFollower: { id: number | null }) =>
                currentFollower.id === id,
        )

        if (currentArray[followerIndex].isAdded) {
            const eligibleFollower = {
                VALUE: currentArray[followerIndex].id || 0,
                LABEL: currentArray[followerIndex].followerName,
                USER_ID: currentArray[followerIndex].partnerId,
            }

            const follwersList = eligibleFollowersList

            follwersList?.push(eligibleFollower)
            if (follwersList) {
                setEligibleFollowersList([...follwersList])
            }

            currentArray.splice(followerIndex, 1)
        } else {
            const inputObject = {
                id: currentArray[followerIndex].id,
                followerName: currentArray[followerIndex].followerName,
                partnerId: currentArray[followerIndex].partnerId,
                assigned: currentArray[followerIndex].assigned,
                delete: true,
                canDelete: currentArray[followerIndex].canDelete,
                isAdded: currentArray[followerIndex].isAdded,
                isChannel: currentArray[followerIndex].isChannel,
            }

            currentArray[followerIndex] = inputObject
        }

        setFollowersList([...currentArray])
    }

    const undoRemove = (id: number) => {
        const currentArray = followersList
        const followerIndex = currentArray.findIndex(
            (currentFollower: { id: number | null }) =>
                currentFollower.id == id,
        )
        const inputObject = {
            id: currentArray[followerIndex].id,
            followerName: currentArray[followerIndex].followerName,
            partnerId: currentArray[followerIndex].partnerId,
            assigned: currentArray[followerIndex].assigned,
            delete: false,
            canDelete: currentArray[followerIndex].canDelete,
            isAdded: currentArray[followerIndex].isAdded,
            isChannel: currentArray[followerIndex].isChannel,
        }

        currentArray[followerIndex] = inputObject
        setFollowersList([...currentArray])
    }

    return (
        <Modal
            title="Add/remove followers"
            visible={isModalVisible}
            destroyOnClose={true}
            onOk={handleSubmit}
            onCancel={handleCloseModal}
            okText="Submit changes"
        >
            <Box direction="col">
                <Box direction="col" paddingBottom="4px">
                    {isSecurePlus && (
                        <SecurePlusIcon
                            iconSize="14px"
                            iconColor={colors.success}
                        />
                    )}
                    <Text
                        className={
                            isSecurePlus
                                ? jobDetailsStyles.modalHeader
                                : followersStyle.modalHeader
                        }
                    >
                        {jobName}
                    </Text>
                </Box>
                <Box direction="col" className={followersStyle.followersBox}>
                    <span className={jobDetailsStyles.heading400}>
                        Add Follower(s)
                    </span>
                    <Row>
                        <Col sm={21} style={{ paddingRight: '4px' }}>
                            <Select
                                showSearch
                                suffixIcon={<SearchOutlined />}
                                mode="multiple"
                                filterOption={(input, option) => {
                                    return (
                                        (option?.key as string)
                                            .toLowerCase()
                                            .indexOf(input.toLowerCase()) >= 0
                                    )
                                }}
                                defaultValue={addedFollowers}
                                value={addedFollowers}
                                style={{ width: '100%' }}
                                placeholder="Type follower"
                                onChange={handleChange}
                                optionLabelProp="label"
                            >
                                {eligibleFollowersList &&
                                    eligibleFollowersList.map(
                                        (follower: EligibleFollower) => {
                                            return (
                                                <Option
                                                    value={follower.VALUE}
                                                    label={follower.LABEL}
                                                    key={follower.LABEL}
                                                >
                                                    <Text>
                                                        {follower.LABEL}
                                                    </Text>
                                                </Option>
                                            )
                                        },
                                    )}
                            </Select>
                        </Col>
                        <Col sm={3}>
                            <Button
                                name="add-followers-button"
                                placement="add_followers_modal"
                                key="add_followers_button"
                                data-test-target="add-followers-button"
                                className={followersStyle.addFollowersButton}
                                onClick={handleAdd}
                            >
                                Add
                            </Button>
                        </Col>
                    </Row>
                </Box>
                <Box direction="col" style={{ marginTop: '16px' }}>
                    <span className={jobDetailsStyles.heading400}>
                        Follower(s)
                    </span>
                    <Box
                        direction="col"
                        className={followersStyle.currentFollowersContainer}
                    >
                        {followersList &&
                            followersList.map(
                                (current: AddFollowerType, index: number) => {
                                    return (
                                        <CurrentFollower
                                            currentFollower={current}
                                            key={index}
                                            handleRemove={handleRemove}
                                            undoRemove={undoRemove}
                                        />
                                    )
                                },
                            )}
                    </Box>
                </Box>
            </Box>
        </Modal>
    )
}

export default AddFollowersModal
